import { NwiAcreageConflictPanel } from './NwiAcreageConflictPanel';
/**
 * SED Variable Browser — view and edit all evidence variables used in the report.
 * Groups by domain, shows values/sources/confidence, inline amendment form.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useStore } from '../../store';
import {
  extractSEDVariables,
  resolveRevertLineageOnSed,
  getSedAmendmentStatusPrecedence,
  computeSedAmendmentDraftSignature,
  readPendingSedSaveEnvelope,
  readPendingSedSaveEnvelopes,
  clearPendingSedSaveEnvelope,
  type PendingSedSaveEnvelope,
} from '../../services/sedAmendments';
import type { SEDVariable, SEDAmendment, AmendmentSource } from '../../types/sedAmendments';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { SiteContext } from '../../types/siteContext';
import type { SiteDocument } from '../../types/siteRag';
import { ChevronDown, ChevronRight, Edit3, Check, X, Upload, Link, User, MapPin, History, Filter, FileText, Sparkles, FileBarChart, ExternalLink, AlertTriangle, RotateCcw, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { FemaRegulatoryContextPanel } from './FemaRegulatoryContextPanel';
import { VariableLineageDrawer } from './VariableLineageDrawer';
import { DocViewerDialog } from './DocViewerDialog';
import { cleanDocFileName } from '../../lib/docFileName';
import { sourceLabel, type SourceTone } from '../../lib/sourceLabel';
import { useSedAmendments } from './useSedAmendments';
// PRE-2 — Provider Evidence Honesty chip vocabulary. Pure helpers.
// PRE-5 — group-header aggregate chip uses aggregateGroupState/types.
import {
  defaultReasonFor,
  ago,
  isAttemptedEmpty,
  aggregateGroupState,
  type GroupChipState,
  type GroupChipTone,
} from '../../lib/providerEvidenceHonesty';
import { getSEDVariableWarnings } from '../../lib/sedVariableSanity';
import { sedVariableOptionsForValue } from '../../lib/sedVariableOptions';
import type { SedSectionId } from '../../types/siteEvidence';
import { bindSedSourceRoleFlags } from '../../lib/sed/sourceRoleReviewBinding';
import { SourceBasisReviewChip } from './SourceBasisReviewChip';

const SOURCE_TONE_CLASSES: Record<SourceTone, string> = {
  doc: 'bg-success/15 text-success border-success/30',
  authored: 'bg-primary/15 text-primary border-primary/30',
  research: 'bg-primary/10 text-primary border-primary/30',
  dam: 'bg-info/15 text-info border-info/30',
  provider: 'bg-success/15 text-success border-success/30',
  missing: 'bg-danger/15 text-danger border-danger/30',
};

interface Props {
  sed: SiteEvidenceDocument | null;
  /** Source inspection in Site Evidence uses plain details without badges. */
  showSourceBadges?: boolean;
  /** Optional — currently unused in the body. Kept for callers that
   *  pass it (per-report variable browser) and to leave room for
   *  amendment-attribution when site-level browsing is the path. */
  reportId?: string;
  /**
   * EV3 (2026-05-15) — when non-empty, the variable list is filtered
   * to rows whose `path` is in the set. The set is populated by
   * SiteEvidenceTab when `?filterReportId={id}` is present in the
   * URL — it captures the variables cited by that specific report
   * (DM4 `linkedDocumentRefs` pre-CT6, CT6 `claims[].sources[]`
   * post-CT6). When the set is `undefined`, the browser renders the
   * full SED (legacy behaviour).
   *
   * Passing an empty `Set` is treated identically to `undefined` — a
   * filtered view that hides every row would be a UX cliff. Tests
   * verify the empty-set fallback explicitly.
   */
  scopedVariablePaths?: Set<string>;
  /** EV3 — human-readable name of the report driving the filter. */
  /** When set, filter chip reads "Filtered to: {scopeLabel}". */
  scopeLabel?: string;
  reportLabel?: string;
  /** EV3 — callback wired to the chip's "Clear filter" link. */
  onClearFilter?: () => void;
  /**
   * Citation_Traceability CT5 — Site Context block carrying user-
   * declared claims. When supplied, the per-variable lineage drawer
   * (replacing the LB3 amendments-only drawer) surfaces every claim
   * that targets the variable's path.
   */
  siteContext?: SiteContext | null;
  /**
   * CT5 — site documents. Used by the lineage drawer to resolve docId
   * → filename for the document rows. When omitted the drawer renders
   * raw docIds.
   */
  documents?: SiteDocument[];
  /**
   * SET-B2 — Site Evidence → Documents bridge. Forwarded to the
   * lineage drawer so document rows become clickable. The host
   * (SiteLandingPage) typically writes `?focus=doc:<id>` + flips
   * activeTab to 'documents' on receipt.
   */
  onOpenDocument?: (docId: string) => void;
  /**
   * UFC-3 — unresolved-conflict count indexed by variable.path
   * (`${domain}.data.${key}`). Drives the per-row "⚠ N sources
   * disagree · [Resolve →]" chip — the reciprocal of the
   * EvidenceConflictsPanel's `[View variable →]` link. Optional;
   * absent map = no chips rendered (legacy mounts).
   */
  conflictCountByPath?: ReadonlyMap<string, number>;
  /**
   * UFC-3 — handler fired when the user clicks the per-row
   * `[Resolve →]` chip. Receives the variable's `(sectionId, fieldKey)`
   * tuple so the parent can deep-link back to the Conflicts panel
   * with the matching group focused. Optional — the chip stays
   * read-only when omitted.
   */
  onResolveConflicts?: (sectionId: string, fieldKey: string) => void;
  /**
   * UFC-3 — variable path to scroll into view + ring-highlight on
   * mount. Driven by the Conflicts panel's `[View variable →]`
   * cross-link via `?focus=variable:<sectionId>.<fieldKey>` (the
   * parent maps that to `<domain>.data.<fieldKey>` before passing
   * down). Also auto-expands the containing domain so the row is
   * visible.
   */
  focusVariablePath?: string | null;
  /** Site id — required for per-section evidence refresh. */
  siteId?: string;
  /** Bump parent SED reload key after apply/revert/section refresh. */
  onReloadSed?: () => void;
}

/**
 * PRV-1 / PRV-2 — Provenance Per Variable strip.
 *
 * Inline lineage strip rendered under every SED variable row whose
 * provenance is doc / context / report — the constitutional answer
 * to "which doc, which page, which paragraph?" without forcing the
 * user to open the deep-history drawer.
 *
 * Split out so the SEDVariableBrowser body stays readable AND so the
 * RDX `report-derived` chip work (sibling pack) can sit alongside
 * the doc-strip without nested branching. Stateless, pure projection
 * of `variable.linkedDocumentRefs` / `authoredClaims` /
 * `derivedFromReport`. The "Open at page" button calls back into the
 * parent's `openDocViewerAt` (the parent owns the DocViewerDialog
 * instance — one mount per browser, not one per row).
 *
 * Empty render contract: when none of the three provenance projections
 * are populated, this component returns `null` and the row layout is
 * identical to the pre-PRV surface (regression invariant #1 from the
 * pack).
 */
const EXCERPT_MAX_CHARS = 200; // R2/R5 — truncate before tooltip handoff.

function truncateExcerpt(text: string, max: number = EXCERPT_MAX_CHARS): string {
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}…`;
}

/**
 * PRE-2 — provider id → display label. Kept inline rather than shared
 * so this renderer owns its own chip text.
 * Keep additions short (≤45 chars) to fit inline next to the reason.
 */
function humanizeProvider(id: string): string {
  switch (id) {
    case 'google-maps-elevation':
      return 'Google Maps Elevation API';
    case 'usgs-epqs':
      return 'USGS EPQS';
    case 'site-profile':
      return 'Site Profile';
    default:
      return id;
  }
}

/**
 * DC-2 / EAS-4 — format the per-stage cascade audit trail into a
 * single-line tooltip phrase. Reads:
 *   "Tried 4 strategies: vertex 0/4 returned · in-polygon-grid 18/25
 *    returned · stopped (sufficient samples)"
 *
 * When the cascade short-circuits on the first non-empty stage, the
 * subsequent stages don't appear in the array — we therefore append
 * "stopped (sufficient samples)" only if the LAST recorded stage was
 * a success (pointsReturned > 0). All-empty cascades omit the suffix
 * so the phrase ends honestly on the final attempt.
 *
 * Returns `null` (NOT empty string) when `stages` is undefined or
 * empty so the caller can branch on the legacy / DC-2 channel cleanly.
 */
function formatAttemptStages(
  stages: SEDVariable['attemptStages'],
): string | null {
  if (!stages || stages.length === 0) return null;
  const parts = stages.map(s => `${s.stage} ${s.pointsReturned}/${s.pointsRequested} returned`);
  const last = stages[stages.length - 1];
  const stoppedEarly = last.pointsReturned > 0 && stages.length < 4;
  if (stoppedEarly) parts.push('stopped (sufficient samples)');
  return `Tried ${stages.length} strateg${stages.length === 1 ? 'y' : 'ies'}: ${parts.join(' · ')}`;
}

function formatActorLabel(actor: { uid: string; email?: string } | string | undefined): string {
  if (!actor) return 'Site Context';
  if (typeof actor === 'string') return actor;
  return actor.email || actor.uid;
}

interface VariableProvenanceStripProps {
  variable: SEDVariable;
  resolveDocName: (docId: string) => string;
  openDocViewerAt: (docId: string, pageNumber?: number) => void;
}

const VariableProvenanceStrip: React.FC<VariableProvenanceStripProps> = ({
  variable,
  resolveDocName,
  openDocViewerAt,
}) => {
  const refs = variable.linkedDocumentRefs ?? [];
  const primaryRef = refs[0];
  const supportingRefs = refs.slice(1);
  const claim = variable.authoredClaims?.[0];
  const report = variable.derivedFromReport;

  // PRV-1 — only the relevant strip surfaces per status. Doc strip
  // renders for `verified-from-doc` whenever ≥1 ref carries enough
  // data to be meaningful (docId is mandatory; the rest is optional).
  const shouldRenderDoc = (variable.status === 'verified-from-doc' || !!variable.documentFieldSource) && !!primaryRef;
  // PRV-2 — context strip needs at least the sourceSpan; an empty
  // claim is noise.
  const shouldRenderContext = variable.status === 'verified-from-context' && !!claim && !!claim.sourceSpan;
  // PRV-2 — report strip needs the reportId at minimum.
  const shouldRenderReport = variable.status === 'verified-from-report' && !!report;

  if (!shouldRenderDoc && !shouldRenderContext && !shouldRenderReport) return null;

  return (
    <div
      data-testid={`variable-lineage-${variable.path}`}
      className="mt-1 type-body text-text-secondary"
    >
      {shouldRenderDoc && primaryRef && (
        <>
          <div className="flex items-center gap-1.5 flex-wrap">
            <FileText className="w-3 h-3 shrink-0 text-text-muted" aria-hidden="true" />
            <span className="font-medium text-text-primary">{resolveDocName(primaryRef.docId)}</span>
            {primaryRef.pageNumber != null && (
              <span className="text-text-muted">· page {primaryRef.pageNumber}</span>
            )}
            {primaryRef.sectionHeading && (
              <span
                className="text-text-muted"
                title={primaryRef.sectionHeading}
              >
                · §{truncateExcerpt(primaryRef.sectionHeading, 60)}
              </span>
            )}
            <button
              type="button"
              data-testid={`open-source-${variable.path}`}
              onClick={() => openDocViewerAt(primaryRef.docId, primaryRef.pageNumber)}
              className="ml-1 underline text-primary hover:text-primary/80 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
              aria-label={`Open ${resolveDocName(primaryRef.docId)} at ${
                primaryRef.pageNumber != null ? `page ${primaryRef.pageNumber}` : 'the source page'
              }`}
            >
              Open at page
            </button>
          </div>
          {primaryRef.excerptPreview && (
            <div
              className="mt-0.5 ml-4 italic text-text-muted border-l-2 border-border pl-2"
              title={primaryRef.excerptPreview}
            >
              "{truncateExcerpt(primaryRef.excerptPreview)}"
            </div>
          )}
          {supportingRefs.length > 0 && (
            <details className="ml-4 mt-0.5">
              <summary
                className="cursor-pointer text-text-muted hover:text-text-primary"
                data-testid={`variable-lineage-${variable.path}-more-summary`}
              >
                {supportingRefs.length} more supporting ref{supportingRefs.length === 1 ? '' : 's'}
              </summary>
              <div className="mt-1 space-y-1">
                {supportingRefs.map((r, i) => (
                  <div
                    key={`${r.docId}-${i}`}
                    className="flex items-center gap-1.5 flex-wrap"
                    data-testid={`variable-lineage-${variable.path}-supporting-${i}`}
                  >
                    <FileText className="w-3 h-3 shrink-0 text-text-muted" aria-hidden="true" />
                    <span>{resolveDocName(r.docId)}</span>
                    {r.pageNumber != null && (
                      <span className="text-text-muted">· page {r.pageNumber}</span>
                    )}
                    <button
                      type="button"
                      onClick={() => openDocViewerAt(r.docId, r.pageNumber)}
                      className="ml-1 underline text-primary hover:text-primary/80 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
                      aria-label={`Open ${resolveDocName(r.docId)} at ${
                        r.pageNumber != null ? `page ${r.pageNumber}` : 'the source page'
                      }`}
                    >
                      Open
                    </button>
                  </div>
                ))}
              </div>
            </details>
          )}
        </>
      )}

      {shouldRenderContext && claim && (
        <div className="flex items-start gap-1.5">
          <Sparkles className="w-3 h-3 shrink-0 mt-0.5 text-primary" aria-hidden="true" />
          <div className="min-w-0">
            <div className="text-text-primary">
              <span className="font-medium">Authored</span>
              {' '}by {formatActorLabel(claim.writtenBy)}
              {claim.lastWrittenAt && (
                <span className="text-text-muted"> · {claim.lastWrittenAt.slice(0, 10)}</span>
              )}
            </div>
            <div
              className="italic text-text-muted border-l-2 border-border pl-2 mt-0.5"
              title={claim.sourceSpan}
            >
              "{truncateExcerpt(claim.sourceSpan)}"
            </div>
          </div>
        </div>
      )}

      {shouldRenderReport && report && (
        <div className="flex items-center gap-1.5 flex-wrap">
          <FileBarChart className="w-3 h-3 shrink-0 text-info" aria-hidden="true" />
          <span className="font-medium text-text-primary">
            {report.reportType} v{report.reportVersion}
          </span>
          {Number.isFinite(report.generatedAt) && report.generatedAt > 0 && (
            <span className="text-text-muted">
              · {new Date(report.generatedAt).toISOString().slice(0, 10)}
            </span>
          )}
          {report.sectionId && (
            <span className="text-text-muted">· §{report.sectionId}</span>
          )}
          {report.verdict && (
            <span
              className="text-text-muted"
              title={report.verdict}
            >
              · {truncateExcerpt(report.verdict, 80)}
            </span>
          )}
          {/* Report deep-link is deferred to a follow-up — the report
              id alone isn't enough to route without the parent's nav
              context. PRV-FOLLOWUP-OPEN-PRIOR-REPORT tracks it. */}
          <ExternalLink className="w-2.5 h-2.5 shrink-0 text-text-muted opacity-50" aria-hidden="true" />
        </div>
      )}
    </div>
  );
};

export function SEDVariableBrowser({
  sed,
  showSourceBadges = true,
  scopedVariablePaths,
  scopeLabel,
  reportLabel,
  onClearFilter,
  siteContext,
  documents,
  onOpenDocument,
  conflictCountByPath,
  onResolveConflicts,
  focusVariablePath,
  siteId,
  onReloadSed,
}: Props) {
  // Reconcile: keep SET-B2's onOpenDocument prop (lineage drawer bridge)
  // AND PH-C4's removal of fetchSEDAmendments destructure (lifted to
  // useSedAmendments hook below).
  const { amendmentState, saveSEDAmendment, revertSedAmendment, acceptSEDVariable, refreshSiteBaseline } = useStore();
  const refreshSedSections = useStore(s => s.refreshSedSections);
  // Live SED source-role review (Stage 2) — text-free advisory flags on the
  // owning site doc. Bound to THIS SED: shown only while the sidecar names
  // this sed id and each flag's evidenceItemHash still matches the leaf.
  // Neutral chip only; never counted in group chips, readiness or anomalies.
  const sourceRoleSidecar = useStore(s => s.siteRag?.sites?.find(site => site.sedId === sed?.id)?.sedSourceRoleReview);
  const sourceRoleFlags = useMemo(() => bindSedSourceRoleFlags(sed, sourceRoleSidecar), [sed, sourceRoleSidecar]);
  // AMP-3 — confirmation-dialog state for the Revert affordance. We
  // keep the row's variable + current-value pair in scope so the
  // dialog can render a meaningful confirmation ("Revert SD1 to 0.25 g
  // — current value 0.30 g will be archived as historical"). Cleared
  // on confirm / cancel so a second click reopens with fresh state.
  const [revertCandidate, setRevertCandidate] = useState<{
    variable: SEDVariable;
    originalValue: unknown;
    siteId: string;
    lineageError?: string | null;
  } | null>(null);
  const [isReverting, setIsReverting] = useState(false);
  const [variables, setVariables] = useState<SEDVariable[]>([]);
  // SET-Q2 — multi-expand domains. Single-select forced users comparing
  // two domains to scroll repeatedly. `Set<string>` keeps multiple panels
  // open and gives natural toggle semantics.
  const [expandedDomains, setExpandedDomains] = useState<Set<string>>(new Set());
  const toggleDomain = useCallback((domain: string) => {
    setExpandedDomains(prev => {
      const next = new Set(prev);
      if (next.has(domain)) next.delete(domain);
      else next.add(domain);
      return next;
    });
  }, []);
  const [editingPath, setEditingPath] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [editReason, setEditReason] = useState('');
  const [editSourceType, setEditSourceType] = useState<AmendmentSource['type']>('expert-judgment');
  const [editSourceLabel, setEditSourceLabel] = useState('');
  const [editSourceUrl, setEditSourceUrl] = useState('');
  // AMP-1 — track whether the user has touched ANY field in the
  // amend form before painting the red borders / inline error strip.
  // The form opens with empty reason + empty source description; we
  // shouldn't shout at the user with red asterisks the moment the
  // form appears. The flag flips on the first onChange of any field
  // (value / reason / source-type / source-description / source-url)
  // and resets when the form opens (`startEdit`) so a re-amend after
  // a save doesn't carry stale interaction state.
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  // AMP-1 — submit-in-flight flag so the disabled state name carries
  // the in-flight reason ("Saving…") rather than the validation
  // reason ("Source description needed") while the network round-trip
  // is in progress. Prevents double-submits AND gives users a visual
  // confirmation the click was received.
  const [isSubmitting, setIsSubmitting] = useState(false);
  // LB3-2 — per-variable amendment-history drawer state. Keyed by
  // variable path; one open at a time so the panel doesn't avalanche.
  const [expandedHistoryPath, setExpandedHistoryPath] = useState<string | null>(null);
  // LB3-3 — header amendment-density filter. When true, the variable
  // list narrows to only rows that have at least one amendment in
  // history. Persisted as a transient toggle (no storage).
  const [showAmendedOnly, setShowAmendedOnly] = useState(false);
  const [showFlaggedOnly, setShowFlaggedOnly] = useState(false);
  const [refreshingSectionPath, setRefreshingSectionPath] = useState<string | null>(null);
  // "Accept value as correct" — in-flight guard so the one-click accept can't
  // be double-fired while the Firestore write is pending.
  const [acceptingPath, setAcceptingPath] = useState<string | null>(null);
  const [editIssueType, setEditIssueType] = useState<SEDAmendment['issueType']>(undefined);
  // PRV-1 — open-at-page dialog state. Local to this browser so a
  // click on one row's [Open at page] button doesn't leak to another
  // row's strip. The viewer mounts a single DocViewerDialog instance
  // and switches its `doc` + `initialPage` props on each click.
  const [viewerOpenDoc, setViewerOpenDoc] = useState<SiteDocument | null>(null);
  const [viewerInitialPage, setViewerInitialPage] = useState<number | undefined>(undefined);
  const closeViewer = useCallback(() => {
    setViewerOpenDoc(null);
    setViewerInitialPage(undefined);
  }, []);
  // PRV-1 — docId → SiteDocument lookup, memoised on the documents
  // prop. Used by the inline lineage strip to resolve filenames and
  // to open the right doc in DocViewerDialog. Falls back to the raw
  // docId when no match (legacy SEDs that linked an evicted doc).
  const docById = useMemo(() => {
    const m = new Map<string, SiteDocument>();
    for (const d of documents ?? []) m.set(d.id, d);
    return m;
  }, [documents]);
  const resolveDocName = useCallback(
    (docId: string) => {
      const d = docById.get(docId);
      return d ? cleanDocFileName(d.fileName) : docId;
    },
    [docById],
  );
  const openDocViewerAt = useCallback(
    (docId: string, pageNumber?: number) => {
      const d = docById.get(docId);
      if (!d) {
        // R4 — graceful fallback when the linked doc is no longer on
        // this site (evicted upload, share/import that didn't include
        // the original). Strip stays usable for diligence; user just
        // can't deep-link until the doc is re-uploaded.
        toast?.('Source document is not available on this site.');
        return;
      }
      setViewerOpenDoc(d);
      setViewerInitialPage(typeof pageNumber === 'number' && pageNumber > 0 ? pageNumber : undefined);
    },
    [docById],
  );

  // PH-C4 — dedupe SED-amendment fetch across mounts. The parent that
  // owns the SED lifecycle (SiteLandingPage) also calls
  // useSedAmendments(sedId); the hook collapses all of them onto a
  // single Firestore round-trip per sedId (in-flight registry +
  // already-loaded short-circuit). Replaces the prior bare effect
  // `if (sed.id) fetchSEDAmendments(sed.id)` with `[sed.id]` deps that
  // omitted `fetchSEDAmendments` (LLM-deps-miss called out in the C4
  // pack quote).
  useSedAmendments(sed?.id ?? '');

  const owningSiteRecord = useStore(s =>
    s.siteRag?.sites?.find(site => (siteId ? site.id === siteId : site.sedId === sed?.id)),
  );
  const [currentActorUid, setCurrentActorUid] = useState<string>('');
  const [pendingEnvelopeRevision, setPendingEnvelopeRevision] = useState(0);
  const [unresolvedRefreshByPath, setUnresolvedRefreshByPath] = useState<
    Record<string, { operationId: string; message: string }>
  >({});
  const [refreshingBaselinePath, setRefreshingBaselinePath] = useState<string | null>(null);
  const draftSessionRef = useRef(0);
  const activeSubmissionSeqRef = useRef(0);
  const activeSubmissionOperationIdRef = useRef<string | null>(null);
  const editingPathRef = useRef<string | null>(null);
  editingPathRef.current = editingPath;
  const activeDraftSubmissionRef = useRef<{
    draftSignature: string;
    sedId: string;
    variablePath: string;
    actorUid: string;
    amendment: SEDAmendment;
    operationId?: string;
    expectedSedVersion?: number;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    void import('../../firebase')
      .then(m => {
        if (!cancelled && m.auth?.currentUser?.uid) {
          setCurrentActorUid(m.auth.currentUser.uid);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [sed?.id]);

  const effectiveAmendmentsForBrowser = useMemo<SEDAmendment[]>(() => {
    const byId = new Map<string, SEDAmendment>();
    for (const a of [
      ...((owningSiteRecord?.sedAmendmentHistory ?? []) as SEDAmendment[]),
      ...((amendmentState?.amendments ?? []) as SEDAmendment[]),
    ]) {
      if (!a || !a.id) continue;
      if (sed?.id && a.sedId && a.sedId !== sed.id) continue;
      const prev = byId.get(a.id);
      if (!prev) {
        byId.set(a.id, a);
      } else {
        const winStatus =
          getSedAmendmentStatusPrecedence(a.status) >= getSedAmendmentStatusPrecedence(prev.status)
            ? a.status
            : prev.status;
        byId.set(a.id, { ...prev, ...a, status: winStatus });
      }
    }
    return [...byId.values()];
  }, [owningSiteRecord?.sedAmendmentHistory, amendmentState?.amendments, sed?.id]);

  useEffect(() => {
    if (!sed) {
      setVariables([]);
      return;
    }
    // PRV-2 — thread siteContext through so the extractor can attach
    // authored-claim provenance to every row whose path matches an
    // ExtractedClaim. Falls back to undefined ⇒ no claim projection
    // (legacy mounts without a Site Context payload).
    const vars = extractSEDVariables(
      sed,
      effectiveAmendmentsForBrowser,
      siteContext ?? null,
    );
    setVariables(vars);
  }, [sed, effectiveAmendmentsForBrowser, siteContext]);

  // LB3-3 — amendment-density count. A variable is "amended" when it
  // has at least one entry in `amendmentHistory`.
  const amendedCount = useMemo(
    () => variables.filter(v => v.isUserAmended || (v.amendmentHistory?.length ?? 0) > 0).length,
    [variables],
  );
  const flaggedCount = useMemo(
    () => variables.filter(v =>
      (v.amendmentHistory ?? []).some(a => a.issueType != null),
    ).length,
    [variables],
  );

  // UFC-3 — when the Conflicts panel's [View variable →] cross-link
  // arrives with a focusVariablePath, expand the containing domain so
  // the row is visible. R6 mitigation: ALSO clear the amended-only
  // filter so a deep-link can't be hidden by a stale filter state.
  // The scroll-into-view effect below picks up the row once it's in
  // the DOM.
  useEffect(() => {
    if (!focusVariablePath) return;
    const domain = focusVariablePath.split('.')[0];
    if (!domain) return;
    setExpandedDomains(prev => {
      if (prev.has(domain)) return prev;
      const next = new Set(prev);
      next.add(domain);
      return next;
    });
    setShowAmendedOnly(false);
    setShowFlaggedOnly(false);
  }, [focusVariablePath]);

  // UFC-3 — ref attached to the focused variable row. Driven by the
  // ref-callback pattern (vs. createRef + manual lookup) so React
  // hands us the freshly-mounted node on the same paint frame the
  // domain expands.
  const focusedRowRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!focusVariablePath) return;
    const handle = window.setTimeout(() => {
      const el = focusedRowRef.current;
      if (!el) return;
      try {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-2', 'ring-warning', 'ring-offset-2');
        window.setTimeout(() => {
          el.classList.remove('ring-2', 'ring-warning', 'ring-offset-2');
        }, 2800);
      } catch { /* best-effort */ }
    }, 250);
    return () => window.clearTimeout(handle);
  }, [focusVariablePath, variables]);

  // EV3 + LB3-3 — compose both filters. First narrow to the report-scoped
  // set when `?filterReportId={…}` is active (EV3), then optionally narrow
  // again to amended-only rows (LB3-3). An empty scopedVariablePaths Set
  // is treated as a no-op (renders the full SED).
  const isFiltered = !!scopedVariablePaths && scopedVariablePaths.size > 0;
  const visibleVariables = useMemo(
    () => {
      const scoped = isFiltered
        ? variables.filter(v => scopedVariablePaths!.has(v.path))
        : variables;
      let list = showAmendedOnly
        ? scoped.filter(v => v.isUserAmended || (v.amendmentHistory?.length ?? 0) > 0)
        : scoped;
      if (showFlaggedOnly) {
        list = list.filter(v =>
          (v.amendmentHistory ?? []).some(a => a.issueType != null),
        );
      }
      return list;
    },
    [variables, isFiltered, scopedVariablePaths, showAmendedOnly, showFlaggedOnly],
  );

  // Group by domain — operate on the (possibly filtered) visible list
  // so domains with zero cited variables drop out of the render.
  const domains = [...new Set(visibleVariables.map(v => v.domain))];
  const byDomain = new Map<string, SEDVariable[]>();
  for (const v of visibleVariables) {
    if (!byDomain.has(v.domain)) byDomain.set(v.domain, []);
    byDomain.get(v.domain)!.push(v);
  }

  const pendingCount = effectiveAmendmentsForBrowser.filter(a => a.status === 'pending').length;

  const resolveEnvelopeScopeForVariable = useCallback(
    (variablePath: string, explicitActorUid?: string) => {
      const actorUid = explicitActorUid || currentActorUid;
      const resolvedSiteId = siteId || owningSiteRecord?.id || sed?.id || '';
      const rawOwner = (owningSiteRecord as { _ownerUid?: string } | undefined)?._ownerUid;
      const ownerUid =
        typeof rawOwner === 'string' && rawOwner.trim() ? rawOwner.trim() : actorUid;
      return {
        actorUid,
        ownerUid,
        siteId: resolvedSiteId,
        sedId: sed?.id ?? '',
        variablePath,
      };
    },
    [currentActorUid, siteId, owningSiteRecord, sed?.id],
  );

  const startEdit = useCallback((v: SEDVariable) => {
    draftSessionRef.current += 1;
    activeSubmissionSeqRef.current += 1;
    activeSubmissionOperationIdRef.current = null;
    const scope = resolveEnvelopeScopeForVariable(v.path);
    const persisted = scope.actorUid && scope.sedId ? readPendingSedSaveEnvelope(scope) : null;
    editingPathRef.current = v.path;
    setEditingPath(v.path);
    if (persisted) {
      setEditValue(String(persisted.amendment.newValue ?? ''));
      setEditReason(persisted.amendment.reason ?? '');
      setEditSourceType(persisted.amendment.source?.type ?? 'expert-judgment');
      setEditSourceLabel(persisted.amendment.source?.label ?? '');
      setEditSourceUrl(persisted.amendment.source?.url ?? '');
      setEditIssueType(persisted.amendment.issueType);
      activeDraftSubmissionRef.current = {
        draftSignature: persisted.draftSignature,
        sedId: persisted.sedId,
        variablePath: persisted.variablePath,
        actorUid: persisted.actorUid,
        amendment: persisted.amendment,
        operationId: persisted.operationId,
        expectedSedVersion: persisted.expectedSedVersion,
      };
      setHasUserInteracted(true);
    } else {
      setEditValue(String(v.value ?? ''));
      setEditReason('');
      setEditSourceType('expert-judgment');
      setEditSourceLabel('');
      setEditSourceUrl('');
      setEditIssueType(undefined);
      activeDraftSubmissionRef.current = null;
      setHasUserInteracted(false);
    }
    setIsSubmitting(false);
  }, [resolveEnvelopeScopeForVariable]);

  const cancelEdit = useCallback(() => {
    // Closing the editor after an uncertain or in-flight response must retain
    // the persisted unresolved operation envelope so it can be recovered via
    // Retry Save, Amend reopen, or after reload. Only explicit Discard or a
    // verified server response removes it.
    draftSessionRef.current += 1;
    activeSubmissionSeqRef.current += 1;
    activeSubmissionOperationIdRef.current = null;
    activeDraftSubmissionRef.current = null;
    editingPathRef.current = null;
    setPendingEnvelopeRevision(r => r + 1);
    setEditingPath(null);
    setHasUserInteracted(false);
    setIsSubmitting(false);
    setEditIssueType(undefined);
  }, []);

  const startFlagIncorrect = useCallback((v: SEDVariable) => {
    draftSessionRef.current += 1;
    activeSubmissionSeqRef.current += 1;
    activeSubmissionOperationIdRef.current = null;
    activeDraftSubmissionRef.current = null;
    editingPathRef.current = v.path;
    setEditingPath(v.path);
    setEditValue(String(v.value ?? ''));
    setEditReason('Incorrect auto-extraction — provider value does not match site evidence.');
    setEditSourceType('expert-judgment');
    setEditSourceLabel('Flagged as incorrect extraction');
    setEditSourceUrl('');
    setEditIssueType('mis-extraction');
    setHasUserInteracted(true);
    setIsSubmitting(false);
  }, []);

  // "Accept value as correct" — one-click positive review. Records a
  // non-mutating `accepted` breadcrumb (no form, no value change, no SED
  // version bump). The store pushes the record into amendmentState, which
  // re-extracts the variable so the "Accepted" badge appears immediately.
  const handleAcceptCorrect = useCallback(async (v: SEDVariable) => {
    if (!sed || acceptingPath === v.path) return;
    setAcceptingPath(v.path);
    try {
      const result = await acceptSEDVariable({
        sedId: sed.id,
        sedVersion: sed.version,
        variablePath: v.path,
        variableLabel: v.label,
        domain: v.domain,
        value: v.value,
        unit: v.unit,
      });
      if (!result.ok) {
        toast?.(`Could not accept value: ${result.error}`);
        return;
      }
      toast?.(`Marked "${v.label}" as reviewed & correct.`);
    } finally {
      setAcceptingPath(null);
    }
  }, [sed, acceptingPath, acceptSEDVariable]);

  const refreshVariableSection = useCallback(async (v: SEDVariable) => {
    if (!siteId) {
      toast?.('Section refresh requires site context.');
      return;
    }
    const sectionId = v.domain as SedSectionId;
    if (refreshingSectionPath === v.path) return;
    setRefreshingSectionPath(v.path);
    try {
      await refreshSedSections(siteId, [sectionId]);
      toast?.(`Refreshed ${v.domainLabel} evidence.`);
      onReloadSed?.();
    } catch {
      toast?.(`Failed to refresh ${v.domainLabel} section.`);
    } finally {
      setRefreshingSectionPath(null);
    }
  }, [siteId, refreshSedSections, refreshingSectionPath, onReloadSed]);

  // AMP-1 — compute the list of blocking required fields. The same
  // predicate drives:
  //   - the Save button's `disabled` attribute,
  //   - the dynamic button label ("Save (Reason for change needed)"),
  //   - the inline error strip below the form,
  //   - the per-field aria-invalid + red-border treatment.
  // Source description is required so the audit trail always carries
  // SOMETHING the regenerator / reviewer can cite — even an
  // "Engineering judgment, Q2 2026 review" string is more useful than
  // an empty source. External-link source type additionally requires
  // a non-empty URL.
  const blockingFields = useMemo(() => {
    const blockers: string[] = [];
    if (editReason.trim().length === 0) blockers.push('Reason for change');
    if (editSourceLabel.trim().length === 0) blockers.push('Source description');
    if (editSourceType === 'external-link' && editSourceUrl.trim().length === 0) {
      blockers.push('Source URL');
    }
    return blockers;
  }, [editReason, editSourceLabel, editSourceType, editSourceUrl]);
  const isSubmittable = !isSubmitting && blockingFields.length === 0;
  const blockerMessage = blockingFields.length === 0
    ? null
    : blockingFields.length === 1
      ? `${blockingFields[0]} is required.`
      : `Required: ${blockingFields.join(', ')}.`;

  const submitAmendment = useCallback(async (v: SEDVariable) => {
    if (!sed) return;
    // AMP-1 — light up the per-field error treatment if the user
    // clicks Save before filling everything. The Save button is
    // disabled when blockers exist; this guard handles the edge case
    // where the click landed before React applied the disabled
    // attribute (test envs / fast clicks).
    if (blockingFields.length > 0) {
      setHasUserInteracted(true);
      return;
    }

    const auth = (await import('../../firebase')).auth;
    const user = auth.currentUser;
    if (!user) return;

    // Parse value to appropriate type with validation
    let newValue: any = editValue;
    if (typeof v.value === 'number') {
      const parsed = parseFloat(editValue);
      if (!isFinite(parsed)) {
        toast?.('Invalid number — please enter a numeric value');
        return;
      }
      newValue = parsed;
    }
    if (typeof v.value === 'boolean') newValue = editValue === 'true';

    const resolvedSourceLabel = editSourceLabel || editSourceType.replace('-', ' ');
    const resolvedSourceUrl = editSourceType === 'external-link' ? editSourceUrl : undefined;
    const currentDraftSignature = computeSedAmendmentDraftSignature({
      variablePath: v.path,
      newValue,
      reason: editReason,
      sourceType: editSourceType,
      sourceLabel: resolvedSourceLabel,
      sourceUrl: resolvedSourceUrl,
      issueType: editIssueType,
    });
    const scope = resolveEnvelopeScopeForVariable(v.path, user.uid);
    const matchingPersistedEnvelope = readPendingSedSaveEnvelope(scope, {
      draftSignature: currentDraftSignature,
    });
    const inMemoryDraft = activeDraftSubmissionRef.current;

    let amendment: SEDAmendment;
    let explicitOperationId: string | undefined;
    let explicitExpectedSedVersion: number | undefined;
    if (matchingPersistedEnvelope) {
      amendment = matchingPersistedEnvelope.amendment;
      explicitOperationId = matchingPersistedEnvelope.operationId;
      explicitExpectedSedVersion = matchingPersistedEnvelope.expectedSedVersion;
    } else if (
      inMemoryDraft &&
      inMemoryDraft.sedId === sed.id &&
      inMemoryDraft.variablePath === v.path &&
      inMemoryDraft.actorUid === user.uid &&
      inMemoryDraft.draftSignature === currentDraftSignature
    ) {
      amendment = inMemoryDraft.amendment;
      explicitOperationId = inMemoryDraft.operationId;
      explicitExpectedSedVersion = inMemoryDraft.expectedSedVersion;
    } else {
      const liveSite = useStore.getState().siteRag?.sites?.find(
        s => (siteId ? s.id === siteId : s.sedId === sed.id),
      );
      const liveSedVersion =
        liveSite?.sedId === sed.id && typeof liveSite.sedVersion === 'number'
          ? liveSite.sedVersion
          : sed.version;
      const liveHistory = Array.isArray(liveSite?.sedAmendmentHistory)
        ? (liveSite.sedAmendmentHistory as SEDAmendment[])
        : (v.amendmentHistory ?? []);
      const latestAppliedForVar = liveHistory.find(
        a => a.sedId === sed.id && a.variablePath === v.path && a.status === 'applied',
      );
      amendment = {
        id: `amend-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        sedId: sed.id,
        sedVersion: liveSedVersion,
        variablePath: v.path,
        variableLabel: v.label,
        domain: v.domain,
        oldValue:
          latestAppliedForVar !== undefined
            ? latestAppliedForVar.newValue
            : (v.extractedValue !== undefined ? v.extractedValue : v.value),
        newValue,
        unit: v.unit,
        reason: editReason,
        source: {
          type: editSourceType,
          label: resolvedSourceLabel,
          url: resolvedSourceUrl,
        },
        createdBy: user.uid,
        createdByEmail: user.email || '',
        createdAt: new Date().toISOString(),
        status: 'pending',
        ...(editIssueType ? { issueType: editIssueType } : {}),
      };
      activeDraftSubmissionRef.current = {
        draftSignature: currentDraftSignature,
        sedId: sed.id,
        variablePath: v.path,
        actorUid: user.uid,
        amendment,
      };
    }

    const submittedSessionId = draftSessionRef.current;
    const submittedSeq = ++activeSubmissionSeqRef.current;
    const submittedVariablePath = v.path;
    const submittedDraftSignature = currentDraftSignature;
    const submittedAmendmentId = amendment.id;

    // AMP-1 — flip the in-flight flag so the button reads "Saving…"
    // and double-clicks are no-ops. Clear in `finally` so a network
    // failure leaves the form interactive for retry.
    setIsSubmitting(true);
    try {
      // SA1 — saveSEDAmendment returns a discriminated union. On
      // failure, surface the error via toast and KEEP THE FORM OPEN so
      // the user doesn't lose their draft. Only close on success.
      const saveOpts =
        explicitOperationId || typeof explicitExpectedSedVersion === 'number'
          ? {
              ...(explicitOperationId ? { operationId: explicitOperationId } : {}),
              ...(typeof explicitExpectedSedVersion === 'number'
                ? { expectedSedVersion: explicitExpectedSedVersion }
                : {}),
            }
          : undefined;
      const resultPromise = saveOpts
        ? saveSEDAmendment(amendment, saveOpts)
        : saveSEDAmendment(amendment);
      const createdEnv = readPendingSedSaveEnvelope(scope, {
        draftSignature: submittedDraftSignature,
      });
      const submittedOperationId =
        explicitOperationId ?? createdEnv?.operationId ?? amendment.operationId;
      if (submittedOperationId) {
        activeSubmissionOperationIdRef.current = submittedOperationId;
      }
      const result = await resultPromise;
      const resolvedEnv = readPendingSedSaveEnvelope(
        scope,
        submittedOperationId
          ? { operationId: submittedOperationId }
          : { draftSignature: submittedDraftSignature },
      );
      const effectiveOpId =
        submittedOperationId ?? resolvedEnv?.operationId ?? result.amendment?.operationId;

      const isStillSameEditorSession =
        draftSessionRef.current === submittedSessionId &&
        editingPathRef.current === submittedVariablePath &&
        activeSubmissionSeqRef.current === submittedSeq;

      if (!result.ok) {
        if (result.operationConfirmed && result.refreshRequired) {
          if (effectiveOpId) {
            clearPendingSedSaveEnvelope(scope, effectiveOpId);
          }
          if (
            activeDraftSubmissionRef.current?.operationId === effectiveOpId ||
            activeDraftSubmissionRef.current?.amendment.id === submittedAmendmentId
          ) {
            activeDraftSubmissionRef.current = null;
          }
          setPendingEnvelopeRevision(r => r + 1);
          setUnresolvedRefreshByPath(prev => ({
            ...prev,
            [submittedVariablePath]: {
              operationId: effectiveOpId ?? '',
              message: result.error,
            },
          }));
          if (isStillSameEditorSession) {
            editingPathRef.current = null;
            setEditingPath(null);
            setEditIssueType(undefined);
          }
          toast?.(result.error);
          return;
        }
        if ((result.conflict || !resolvedEnv) && effectiveOpId) {
          clearPendingSedSaveEnvelope(scope, effectiveOpId);
          if (
            activeDraftSubmissionRef.current?.operationId === effectiveOpId ||
            (!activeDraftSubmissionRef.current?.operationId &&
              activeDraftSubmissionRef.current?.amendment.id === submittedAmendmentId)
          ) {
            activeDraftSubmissionRef.current = null;
          }
        } else if (isStillSameEditorSession && resolvedEnv) {
          activeDraftSubmissionRef.current = {
            draftSignature: resolvedEnv.draftSignature,
            sedId: resolvedEnv.sedId,
            variablePath: resolvedEnv.variablePath,
            actorUid: resolvedEnv.actorUid,
            amendment: resolvedEnv.amendment,
            operationId: resolvedEnv.operationId,
            expectedSedVersion: resolvedEnv.expectedSedVersion,
          };
        }
        setPendingEnvelopeRevision(r => r + 1);
        toast?.(`Failed to save amendment: ${result.error}`);
        return;
      }

      if (effectiveOpId) {
        clearPendingSedSaveEnvelope(scope, effectiveOpId);
      }
      if (activeDraftSubmissionRef.current?.amendment.id === submittedAmendmentId) {
        activeDraftSubmissionRef.current = null;
      }
      setUnresolvedRefreshByPath(prev => {
        if (!(submittedVariablePath in prev)) return prev;
        const next = { ...prev };
        delete next[submittedVariablePath];
        return next;
      });
      setPendingEnvelopeRevision(r => r + 1);

      // Only close/reset the inline editor if the user is STILL on this exact
      // draft session and has not closed/reopened or started editing a different draft.
      if (isStillSameEditorSession) {
        editingPathRef.current = null;
        setEditingPath(null);
        setEditIssueType(undefined);
      }
      toast?.(result.applied ? 'Amendment saved and applied to SED.' : 'Amendment saved.');
      if (result.applied && (!editingPathRef.current || isStillSameEditorSession)) {
        onReloadSed?.();
      }
    } finally {
      if (activeSubmissionSeqRef.current === submittedSeq) {
        activeSubmissionOperationIdRef.current = null;
        setIsSubmitting(false);
      }
    }
  }, [blockingFields, editValue, editReason, editSourceType, editSourceLabel, editSourceUrl, editIssueType, sed, siteId, saveSEDAmendment, onReloadSed, resolveEnvelopeScopeForVariable]);

  const handleRefreshAuthoritativeBaseline = useCallback(
    async (variablePath: string) => {
      const targetSiteId = siteId || owningSiteRecord?.id;
      if (!targetSiteId || refreshingBaselinePath === variablePath) return;
      setRefreshingBaselinePath(variablePath);
      try {
        const refreshed = await refreshSiteBaseline(targetSiteId);
        if (!refreshed.site || (refreshed.site.sedId && !refreshed.sed)) {
          toast?.('Could not refresh authoritative site evidence. Please try again.');
          return;
        }
        setUnresolvedRefreshByPath(prev => {
          if (!(variablePath in prev)) return prev;
          const next = { ...prev };
          delete next[variablePath];
          return next;
        });
        onReloadSed?.();
        toast?.('Refreshed authoritative site evidence.');
      } catch (err) {
        toast?.(
          `Failed to refresh authoritative site evidence: ${
            (err as Error)?.message || 'Unknown error'
          }`,
        );
      } finally {
        setRefreshingBaselinePath(null);
      }
    },
    [siteId, owningSiteRecord?.id, refreshingBaselinePath, refreshSiteBaseline, onReloadSed],
  );

  const retryUnresolvedEnvelope = useCallback(
    async (v: SEDVariable, env: PendingSedSaveEnvelope) => {
      if (isSubmitting) return;
      const scope = resolveEnvelopeScopeForVariable(v.path, env.actorUid);
      setIsSubmitting(true);
      try {
        const result = await saveSEDAmendment(env.amendment, {
          operationId: env.operationId,
          expectedSedVersion: env.expectedSedVersion,
        });
        const stillPendingEnv = readPendingSedSaveEnvelope(scope, {
          operationId: env.operationId,
        });
        if (!result.ok) {
          if (result.operationConfirmed && result.refreshRequired) {
            clearPendingSedSaveEnvelope(scope, env.operationId);
            if (activeDraftSubmissionRef.current?.operationId === env.operationId) {
              activeDraftSubmissionRef.current = null;
            }
            setPendingEnvelopeRevision(r => r + 1);
            setUnresolvedRefreshByPath(prev => ({
              ...prev,
              [v.path]: {
                operationId: env.operationId,
                message: result.error,
              },
            }));
            toast?.(result.error);
            return;
          }
          if (result.conflict || !stillPendingEnv) {
            clearPendingSedSaveEnvelope(scope, env.operationId);
            if (activeDraftSubmissionRef.current?.operationId === env.operationId) {
              activeDraftSubmissionRef.current = null;
            }
          }
          setPendingEnvelopeRevision(r => r + 1);
          toast?.(`Failed to save amendment: ${result.error}`);
          return;
        }
        activeDraftSubmissionRef.current = null;
        clearPendingSedSaveEnvelope(scope, env.operationId);
        setUnresolvedRefreshByPath(prev => {
          if (!(v.path in prev)) return prev;
          const next = { ...prev };
          delete next[v.path];
          return next;
        });
        setPendingEnvelopeRevision(r => r + 1);
        toast?.(result.applied ? 'Amendment saved and applied to SED.' : 'Amendment saved.');
        if (result.applied) {
          onReloadSed?.();
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [isSubmitting, resolveEnvelopeScopeForVariable, saveSEDAmendment, onReloadSed],
  );

  const discardUnresolvedEnvelope = useCallback(
    (variablePath: string, expectedOperationId?: string) => {
      const scope = resolveEnvelopeScopeForVariable(variablePath);
      clearPendingSedSaveEnvelope(scope, expectedOperationId);
      if (activeDraftSubmissionRef.current?.variablePath === variablePath) {
        activeDraftSubmissionRef.current = null;
      }
      setPendingEnvelopeRevision(r => r + 1);
    },
    [resolveEnvelopeScopeForVariable],
  );

  if (!sed) {
    return (
      <div
        className="border border-dashed border-border p-4 text-center text-[10px] text-text-muted"
        data-testid="sed-variable-browser-empty"
      >
        Load site evidence before browsing extracted variables.
      </div>
    );
  }

  return (
    <div className="space-y-2 w-full min-w-0">
      {/* SE2 — section header inside the Site Evidence tab. The outer tab
          header already names this surface as the SED, so the inner header
          just names the LIST inside it. Word "Document" deliberately
          dropped to kill the duplicate-title problem. */}
      <div className="flex items-center justify-between border-b border-border-subtle pb-2">
        <div className="space-y-0.5">
          <div className="text-[11px] font-bold uppercase tracking-widest text-text-primary">
            Extracted Variables
          </div>
          <div className="type-body text-text-muted flex items-center gap-1.5 flex-wrap">
            <span>
              {isFiltered
                ? `${visibleVariables.length} of ${variables.length} extractable field${variables.length !== 1 ? 's' : ''} across ${domains.length} domain${domains.length !== 1 ? 's' : ''} · amend any value to override the auto-extracted answer`
                : `${variables.length} extractable field${variables.length !== 1 ? 's' : ''} across ${domains.length} domain${domains.length !== 1 ? 's' : ''} · amend any value to override the auto-extracted answer`
              }
            </span>
            {/* LB3-3 — amendment-density chip. Click to filter to only
                amended rows; click again to show all. */}
            {amendedCount > 0 && (
              <button
                type="button"
                onClick={() => setShowAmendedOnly(prev => !prev)}
                className={`flex items-center gap-1 px-1.5 py-0.5 type-meta uppercase tracking-widest border rounded-md transition-colors ${
                  showAmendedOnly
                    ? 'selected-accent'
                    : 'border-border text-text-secondary hover:text-primary hover:border-primary'
                } focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary`}
                data-testid="sed-variable-amendment-filter"
                aria-pressed={showAmendedOnly}
                aria-label={showAmendedOnly
                  ? `Showing only ${amendedCount} amended variable${amendedCount === 1 ? '' : 's'} — click to show all`
                  : `${amendedCount} variable${amendedCount === 1 ? '' : 's'} amended — click to filter`
                }
              >
                {amendedCount} / {variables.length} amended
              </button>
            )}
            {flaggedCount > 0 && (
              <button
                type="button"
                onClick={() => setShowFlaggedOnly(prev => !prev)}
                className={`flex items-center gap-1 px-1.5 py-0.5 type-meta uppercase tracking-widest border rounded-md transition-colors ${
                  showFlaggedOnly
                    ? 'border-warning text-warning bg-warning/10'
                    : 'border-border text-text-secondary hover:text-warning hover:border-warning'
                } focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-warning`}
                data-testid="sed-variable-flagged-filter"
                aria-pressed={showFlaggedOnly}
              >
                {flaggedCount} flagged
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {pendingCount > 0 && (
            <span className="type-body font-bold px-2 py-0.5 bg-warning/15 text-warning rounded">
              {pendingCount} pending amendment{pendingCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {/* EV3 (2026-05-15) — report-scoped filter chip. Renders only
           when the parent passed a non-empty `scopedVariablePaths` set;
           tells the user the list is filtered and offers a one-click
           clear. Hidden on the unscoped (legacy) Site Evidence tab
           path. */}
      {isFiltered && (
        <div
          className="flex items-center gap-2 px-2 py-1 bg-primary/5 rounded-lg border border-primary/30"
          data-testid="sed-variable-filter-chip"
        >
          <Filter className="w-3 h-3 text-primary shrink-0" aria-hidden="true" />
          <span className="text-[10px] text-text-primary">
            {scopeLabel
              ? `Filtered to: ${scopeLabel}`
              : `Showing ${visibleVariables.length} variable${visibleVariables.length === 1 ? '' : 's'} cited in ${reportLabel ?? 'this report'}`}
          </span>
          {onClearFilter && (
            <button
              type="button"
              onClick={onClearFilter}
              className="ml-auto text-[10px] underline text-primary hover:text-primary/80 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
              data-testid="sed-variable-clear-report-filter"
              aria-label={`Clear filter — show all variables (currently scoped to ${reportLabel ?? 'this report'})`}
            >
              Clear filter
            </button>
          )}
        </div>
      )}

      {/* PRV-1 — single DocViewerDialog instance, opened on demand by
          each row's [Open at page] button. One mount per browser keeps
          the modal stack flat; the doc + initialPage props re-fire when
          the user clicks a different row's button (PdfViewer re-reads
          `initialPage` on doc.id / initialPage change). */}
      <DocViewerDialog
        doc={viewerOpenDoc}
        open={viewerOpenDoc != null}
        onClose={closeViewer}
        initialPage={viewerInitialPage}
      />

      {/* Domain sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="sed-variable-domain-grid">
      {domains.map(domain => {
        const domainVars = byDomain.get(domain) || [];
        const isExpanded = expandedDomains.has(domain);
        const domainLabel = domainVars[0]?.domainLabel || domain;
        const domainPending = domainVars.filter(v => v.pendingAmendment).length;

        // SET-Q2 + PRE-5 — status counts that compose with the PRE-5
        // group-header chip. `anomalousCount` keeps the SET-Q2
        // invariant (anomalies override the chip label) and
        // `unavailableCount` surfaces in the breakdown when legacy
        // unavailable rows co-occur with PRE attempted-empty slots.
        //
        // The legacy `domainWorstStatus` / `verifiedCount` derivation
        // was retired by PRE-5: the chip now reads `attemptResult`
        // directly via `aggregateGroupState` (computed below). Legacy
        // SEDs without `attemptResult` continue to flow through that
        // helper's back-compat default (value-present ⇒ success), so a
        // domain with no PRE attempts still renders VERIFIED — no
        // regression vs the pre-PRE-5 chip on pre-PRE-1 SEDs.
        const anomalousCount = domainVars.filter(v => v.status === 'anomalous').length;
        const unavailableCount = domainVars.filter(v => v.status === 'unavailable').length;

        // ── PRE-5 — group-header aggregate chip ───────────────────────
        //
        // Live-prod symptom on the Wave-7 Sickel SED Variable Browser:
        // every domain rendered a uniform `VERIFIED · {N} verified`
        // chip because the legacy aggregator counted "row exists in
        // SED" instead of consulting `attemptResult`. So `siteProfile`
        // claimed VERIFIED · 7 verified even when every elevation slot
        // carried `attemptResult: 'empty'` — exactly the dishonest
        // header chip the PRE pack set out to fix.
        //
        // PRE-5 rolls up per-slot attemptResult into a state-
        // distinguishing chip (VERIFIED / PARTIALLY VERIFIED / NO DATA
        // / NOT COLLECTED / MIXED). The pure helper sits in
        // `src/lib/providerEvidenceHonesty.ts`; this surface is the
        // first consumer.
        //
        // Composition rules:
        //  1. Anomalous slots ALWAYS win — cross-check failures on a
        //     present value are strictly worse than an attempted-empty.
        //     The chip keeps the existing "ANOMALOUS amber" treatment
        //     so the SET-Q2 invariant (1 anomalous + 19 verified ⇒
        //     ANOMALOUS chip) is preserved.
        //  2. Otherwise, PRE-5 drives the chip: label / tone / subtitle
        //     all come from `aggregateGroupState`. Legacy SEDs without
        //     `attemptResult` fall through to the helper's back-compat
        //     default — value-present ⇒ success, value-missing ⇒
        //     not-attempted — so a pre-PRE-1 SED still surfaces
        //     VERIFIED on a fully-populated domain.
        //  3. Pre-existing `status: 'unavailable'` rows (legacy hard
        //     gap) flow through as `not-attempted` to PRE-5 — same
        //     visual treatment as the existing "unavailable" red chip,
        //     but with the new vocabulary.
        const preAggregate: GroupChipState = aggregateGroupState(
          domainVars.map(v => ({ value: v.value, attemptResult: v.attemptResult })),
        );
        const TONE_CLASS: Record<GroupChipTone, string> = {
          success: 'bg-success/15 text-success',
          amber: 'bg-warning/15 text-warning',
          red: 'bg-danger/15 text-danger',
          muted: 'bg-state-hover text-text-muted',
        };
        // Anomalous-priority override — keep SET-Q2 behaviour intact.
        const chipLabel: string = anomalousCount > 0 ? 'ANOMALOUS' : preAggregate.label;
        const chipTone: GroupChipTone = anomalousCount > 0 ? 'amber' : preAggregate.tone;
        // Subtitle composes both signals so the breakdown stays legible
        // even when anomalous + PRE attempted-empty co-occur on the
        // same domain (e.g. flood with 1 anomalous zone + 1 empty
        // sample-point lookup).
        const breakdownSegments: string[] = [preAggregate.subtitle];
        if (anomalousCount > 0) breakdownSegments.push(`${anomalousCount} anomalous`);
        if (unavailableCount > 0) breakdownSegments.push(`${unavailableCount} unavailable`);
        const chipSubtitle = breakdownSegments.join(' · ');

        return (
          <div key={domain} className="border border-border-subtle min-w-0">
            <button
              className="w-full flex items-center justify-between px-3 py-2 bg-state-hover hover:bg-state-hover transition-colors"
              onClick={() => toggleDomain(domain)}
              aria-expanded={isExpanded}
            >
              <div className="flex items-center gap-2">
                {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                <span className="type-section">{domainLabel}</span>
                <span className="type-body text-text-muted">{domainVars.length} variables</span>
              </div>
              <div className="flex flex-col items-end gap-0.5">
                <div className="flex items-center gap-2">
                  {/* SET-Q3 — removed redundant per-domain warning dot.
                      The panel header chip "{n}/{N} amended" (rendered
                      above) already encodes pending-amendment state for
                      the whole panel; the per-domain dot was a third
                      visual treatment for the same concept. The
                      per-domain count is surfaced as a small chip below. */}
                  {domainPending > 0 && (
                    <span className="type-meta text-warning" title="Pending amendments in this domain">
                      {domainPending} pending
                    </span>
                  )}
                  {/* PRE-5 — group-header aggregate chip. Drives the
                      label + tone from `aggregateGroupState` so a
                      partially-empty group (Sickel siteProfile: 2/7
                      verified · 5 attempted-empty) reads as PARTIALLY
                      VERIFIED amber instead of the dishonest legacy
                      "VERIFIED · 7 verified". Anomalous slots keep
                      their existing amber treatment (composition rule
                      #1 above). `data-group-chip-*` attributes give
                      e2e probes a stable hook on the new vocabulary. */}
                  <span
                    className={`type-meta font-bold px-1.5 py-0.5 rounded ${TONE_CLASS[chipTone]}`}
                    data-group-chip-label={chipLabel}
                    data-group-chip-tone={chipTone}
                    data-group-chip-success={preAggregate.counts.success}
                    data-group-chip-empty={preAggregate.counts.empty}
                    data-group-chip-not-attempted={preAggregate.counts.notAttempted}
                  >
                    {chipLabel}
                  </span>
                </div>
                {/* SET-Q2 + PRE-5 — breakdown so the user can see
                    "2/7 verified · 5 attempted-empty" without expanding
                    the panel. Anomalous + unavailable counts are
                    appended when they co-occur (composition rule #3). */}
                <span className="type-meta text-text-muted" data-group-chip-subtitle={chipSubtitle}>
                  {chipSubtitle}
                </span>
              </div>
            </button>

            {isExpanded && (
              <div className="divide-y divide-primary/5">
                {domain === 'environmental' && <NwiAcreageConflictPanel conflicts={sed?.environmental?.data?.wetlandSourceAcreageConflicts} issues={sed?.environmental?.data?.wetlandAcreageIssues} />}
                {domain === 'flood' && sed?.flood?.data?.regulatoryContext && <FemaRegulatoryContextPanel context={sed.flood.data.regulatoryContext} />}
                {(() => {
                  // Source-role flags on leaves this browser has no row for
                  // (nested values, requirement Populate fields). Rows are
                  // matched against the UNFILTERED list, so a row a filter
                  // hides is never re-listed here, and the list is shown only
                  // in the unfiltered view (a scoped or amended/flagged-only
                  // view never gains out-of-scope chips).
                  if (isFiltered || showAmendedOnly || showFlaggedOnly) return null;
                  const rowPaths = new Set(variables.filter(v => v.domain === domain).map(v => v.path));
                  const unmatched = [...sourceRoleFlags.values()]
                    .filter(flag => flag.sectionId === domain && !rowPaths.has(flag.key));
                  return unmatched.length > 0 ? (
                    <div className="px-3 py-2 flex flex-wrap gap-1.5" data-testid={`sed-source-role-unmatched-${domain}`}>
                      {unmatched.map(flag => (
                        <SourceBasisReviewChip key={flag.key} flag={flag} fieldLabel={flag.fieldPath} />
                      ))}
                    </div>
                  ) : null;
                })()}
                {domainVars.map(v => {
                  // UFC-3 — per-row unresolved-conflict badge. The
                  // index is built off the live SED so the count tracks
                  // resolutions in real time (resolves in the Conflicts
                  // panel decrement the chip on this row).
                  const conflictCount = conflictCountByPath?.get(v.path) ?? 0;
                  const isFocused = focusVariablePath === v.path;
                  // "Accept value as correct" — the row's most-recent review
                  // record (history is newest-first) is a positive acceptance.
                  // Any later amend / flag / revert writes a newer record with a
                  // different status, so the badge clears on its own when the
                  // value moves on.
                  const isAccepted = (v.amendmentHistory ?? [])[0]?.status === 'accepted';
                  // Live SED source-role review chip: a human decision on this
                  // row (accept, amend, flag, revert) at or after the second
                  // read supersedes the advisory, so the chip clears.
                  const sourceRoleFlag = sourceRoleFlags.get(v.path);
                  const reviewedAt = sourceRoleFlag?.reviewedAt;
                  const showSourceRoleChip = Boolean(sourceRoleFlag) && !isAccepted
                    && !(reviewedAt && (v.amendmentHistory ?? []).some(a => typeof a.createdAt === 'string' && a.createdAt >= reviewedAt));
                  // UFC-3 — variable.path is `${domain}.data.${key}`;
                  // the underlying conflict is keyed by `(sectionId,
                  // fieldKey)` which maps to `(domain, key)`. Pull the
                  // tuple back out so the Resolve chip can deep-link
                  // the right group.
                  const pathParts = v.path.split('.');
                  const conflictSectionId = pathParts[0];
                  const conflictFieldKey = pathParts[pathParts.length - 1];
                  return (
                  <div
                    key={v.path}
                    ref={isFocused ? focusedRowRef : undefined}
                    data-testid={`sed-variable-row-${v.path}`}
                    className="px-3 py-2"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] font-medium text-text-muted">{v.label}</span>
                          {v.pendingAmendment && <span className="w-1.5 h-1.5 rounded-full bg-warning" title="Pending amendment" />}
                          {/* UFC-3 — reciprocal "[Resolve →]" chip.
                              Renders only when the variable has ≥1
                              unresolved conflict. Click takes the user
                              back to the EvidenceConflictsPanel scrolled
                              to the matching group. */}
                          {conflictCount > 0 && (
                            <button
                              type="button"
                              onClick={() =>
                                onResolveConflicts?.(conflictSectionId, conflictFieldKey)
                              }
                              disabled={!onResolveConflicts}
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 type-meta uppercase tracking-widest rounded-md border border-warning/50 bg-warning/10 text-warning hover:bg-warning/20 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-warning"
                              data-testid={`sed-variable-conflict-chip-${v.path}`}
                              aria-label={`${conflictCount} source${conflictCount === 1 ? '' : 's'} disagree on ${v.label} — open the conflicts panel`}
                            >
                              <AlertTriangle className="w-2.5 h-2.5" aria-hidden="true" />
                              {conflictCount} source{conflictCount === 1 ? '' : 's'} disagree · Resolve
                            </button>
                          )}
                          {showSourceRoleChip && sourceRoleFlag && (
                            <SourceBasisReviewChip flag={sourceRoleFlag} testId={`sed-variable-source-role-chip-${v.path}`} />
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          {v.isUserAmended && (
                            <span
                              className="type-meta font-bold uppercase tracking-widest px-1 py-0.5 bg-primary/10 text-primary rounded-md border border-primary/30"
                              data-testid={`sed-variable-amended-badge-${v.path}`}
                            >
                              Amended
                            </span>
                          )}
                          {/* "Accept value as correct" — positive-review badge.
                              Not shown alongside "Amended": a fresh amend writes
                              a newer record so isAccepted is already false. */}
                          {isAccepted && (
                            <span
                              className="inline-flex items-center gap-1 type-meta font-bold px-1 py-0.5 bg-success/10 text-success rounded-md border border-success/30"
                              data-testid={`sed-variable-accepted-badge-${v.path}`}
                              title="You reviewed this value and confirmed it is correct"
                            >
                              <Check className="w-2.5 h-2.5" aria-hidden="true" /> Accepted
                            </span>
                          )}
                          <span className="text-[11px] font-mono font-bold text-foreground">
                            {typeof v.value === 'boolean' ? (v.value ? 'Yes' : 'No') :
                             Array.isArray(v.value) ? v.value.join(', ') :
                             v.value ?? '—'}
                          </span>
                          {v.unit && <span className="type-body text-text-secondary">{v.unit}</span>}
                          {(() => {
                            const applied = (v.amendmentHistory ?? []).find(a => a.status === 'applied');
                            const wasVal = applied?.oldValue;
                            if (!v.isUserAmended || wasVal == null) return null;
                            if (wasVal === v.value) return null;
                            return (
                              <span
                                className="type-body text-text-muted line-through"
                                data-testid={`sed-variable-was-value-${v.path}`}
                              >
                                Was: {String(wasVal)}{v.unit ? ` ${v.unit}` : ''}
                              </span>
                            );
                          })()}
                          {(() => {
                            const applied = (v.amendmentHistory ?? []).find(a => a.status === 'applied');
                            if (!applied?.source?.label) return null;
                            return (
                              <span className="type-meta text-text-muted" title={applied.source.label}>
                                · {applied.source.label}
                              </span>
                            );
                          })()}
                          {getSEDVariableWarnings(v).map((w, i) => (
                            <span
                              key={`${v.path}-warn-${i}`}
                              className={`type-meta px-1 py-0.5 border ${
                                w.severity === 'error'
                                  ? 'border-danger/50 bg-danger/10 text-danger'
                                  : 'border-warning/50 bg-warning/10 text-warning'
                              }`}
                              data-testid={`sed-variable-sanity-${v.path}`}
                              title={w.message}
                            >
                              {w.message}
                            </span>
                          ))}
                          {showSourceBadges && (() => {
                            const { label, tone } = sourceLabel(v);
                            return (
                              <span
                                data-testid={`source-chip-${v.path}`}
                                data-tone={tone}
                                className={`inline-flex items-center px-1.5 py-0.5 type-meta uppercase tracking-widest border rounded ${SOURCE_TONE_CLASSES[tone]}`}
                                title={label}
                                aria-label={`Source: ${label}`}
                              >
                                {label}
                              </span>
                            );
                          })()}
                        </div>
                        {/* DCO-4 — clickable AI Research provenance chip.
                            Renders only for slots stamped
                            'verified-from-research' (researchSource is
                            projected onto the variable by
                            extractSEDVariables). Carries the source URL +
                            title + retrieval time + confidence so the
                            user can verify the answer themselves. The
                            constitutional principle says every research
                            fill MUST be auditable — this is the surface.
                        */}
                        {v.researchSource && (
                          <div className="flex items-center gap-1 mt-1 type-body text-text-muted">
                            <span className="px-1.5 py-0.5 bg-primary/10 text-primary uppercase tracking-widest font-bold">
                              AI Research
                            </span>
                            <a
                              href={v.researchSource.sourceUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="truncate max-w-[180px] underline hover:text-primary"
                              title={`${v.researchSource.sourceTitle} — opens in new tab. Confidence: ${Math.round(v.researchSource.confidence * 100)}%`}
                              data-testid={`sed-variable-research-source-${v.path}`}
                            >
                              {v.researchSource.sourceTitle || v.researchSource.sourceUrl}
                            </a>
                            <span className="text-text-secondary">
                              ({Math.round(v.researchSource.confidence * 100)}%)
                            </span>
                          </div>
                        )}
                        {v.damSource && (
                          <div className="flex items-center gap-1 mt-1 type-body text-text-muted">
                            <span
                              className="px-1.5 py-0.5 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 uppercase tracking-widest font-bold"
                              data-testid={`sed-variable-dam-chip-${v.path}`}
                            >
                              DAM
                            </span>
                            <span
                              className="truncate max-w-[220px]"
                              title={`${v.damSource.damEntryId} — confidence ${Math.round(v.damSource.confidence * 100)}%`}
                              data-testid={`sed-variable-dam-source-${v.path}`}
                            >
                              v{v.damSource.damVersion} · {v.damSource.damEntryId.split('/').pop()}
                            </span>
                            <span className="text-text-secondary">
                              ({Math.round(v.damSource.confidence * 100)}%)
                            </span>
                          </div>
                        )}
                        {/* PRE-2 — Provider Evidence Honesty per-row chip.
                            Fires when the section's producer recorded an
                            attemptedProvider AND this row's value is null
                            (the polygon was drawn, the Google Elevation
                            API returned no usable data). Honest alternative
                            to silently surfacing the provider label as if
                            the call had succeeded — that was the customer-
                            flagged "—m · Google Maps Elevation API"
                            dishonest chip. Tooltip explains the failure
                            mode and reminds the user that Amend is still
                            available regardless of provider state. */}
                        {isAttemptedEmpty(v.value, v.attemptedProvider) && (() => {
                          const reason = v.emptyReason ?? defaultReasonFor(v.attemptResult);
                          const attemptedAgoText = ago(v.lastAttemptedAt);
                          // DC-2 — extend the PRE chip tooltip with the
                          // EAS-4 per-stage cascade audit trail when
                          // it's stamped on the variable. Reads like
                          // "Tried 4 strategies: vertex 0/4 returned ·
                          // in-polygon-grid 18/25 returned · stopped
                          // (sufficient samples)". Falls back to the
                          // pre-DC-2 tooltip when attemptStages is
                          // undefined (legacy SEDs).
                          const stagesTooltip = formatAttemptStages(v.attemptStages);
                          const baseTooltip = attemptedAgoText
                            ? `${reason}. Last attempted ${attemptedAgoText}. Click Amend to override manually or wait for auto-retry.`
                            : `${reason}. Click Amend to override manually or wait for auto-retry.`;
                          const tooltip = stagesTooltip
                            ? `${stagesTooltip}. ${baseTooltip}`
                            : baseTooltip;
                          return (
                            <div
                              className="flex items-center gap-1 mt-1 type-body text-warning"
                              data-source-state="attempted-empty"
                              data-testid={`sed-variable-attempted-empty-${v.path}`}
                              data-attempted-provider={v.attemptedProvider}
                              data-attempt-result={v.attemptResult ?? 'empty'}
                              data-attempt-stages-count={v.attemptStages?.length ?? 0}
                              title={tooltip}
                            >
                              <AlertTriangle className="w-2.5 h-2.5 shrink-0" aria-hidden="true" />
                              <span className="font-bold">
                                {humanizeProvider(v.attemptedProvider!)}
                              </span>
                              <span>— {reason}</span>
                              {attemptedAgoText && (
                                <span className="text-text-secondary">· {attemptedAgoText}</span>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                      {v.editable && editingPath !== v.path && (() => {
                        // AMP-3 — show the Revert chip when the row
                        // has at least one APPLIED amendment in its
                        // history. The earliest applied amendment's
                        // `oldValue` is the canonical provider
                        // original we'd restore. `reverted` and
                        // `superseded` rows don't count (they're
                        // already terminal); rows with only `pending`
                        // amendments don't either (no SED change to
                        // revert yet).
                        const appliedHistory = (v.amendmentHistory ?? []).filter(
                          a => a.status === 'applied' && !a.revertedFromAmendmentId && !a.id.startsWith('revert-'),
                        );
                        const canRevert = appliedHistory.length > 0;
                        const revertLineage =
                          canRevert && sed
                            ? resolveRevertLineageOnSed(v.amendmentHistory ?? [], sed.id, v.path)
                            : null;
                        const revertLineageError =
                          revertLineage && !revertLineage.ok
                            ? revertLineage.error
                            : canRevert && !revertLineage
                              ? 'Unresolved amendment lineage.'
                              : null;
                        const originalProviderValue =
                          revertLineage && revertLineage.ok
                            ? revertLineage.originalProviderValue
                            : undefined;
                        return (
                        <div className="flex items-center gap-1">
                          {/* LB3-2 — inline history-drawer toggle. Only
                              renders when the variable has past edits;
                              non-amended rows stay quiet. */}
                          {(v.amendmentHistory?.length ?? 0) > 0 && (
                            <button
                              type="button"
                              onClick={() => setExpandedHistoryPath(prev => prev === v.path ? null : v.path)}
                              className={`flex items-center gap-1 px-1.5 py-0.5 type-meta uppercase tracking-widest border transition-colors ${
                                expandedHistoryPath === v.path
                                  ? 'border-primary text-primary bg-primary/5'
                                  : 'border-border text-text-secondary hover:text-primary hover:border-primary'
                              } focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary`}
                              data-testid={`sed-variable-history-toggle-${v.path}`}
                              aria-expanded={expandedHistoryPath === v.path}
                              aria-label={`${v.amendmentHistory.length} prior edit${v.amendmentHistory.length === 1 ? '' : 's'} for ${v.label}`}
                            >
                              <History className="w-2.5 h-2.5" /> {v.amendmentHistory.length}
                            </button>
                          )}
                          {/* AMP-3 — per-row Revert chip. Surfaces
                              ONLY when ≥1 applied amendment exists
                              for this variable. Click opens a
                              confirmation dialog naming the
                              original provider value + the current
                              value being archived. The button is
                              positioned BEFORE Amend so the most
                              common action (Amend) stays right-most. */}
                          {canRevert && (
                            <button
                              type="button"
                              onClick={() => {
                                // Resolve the site that owns this SED
                                // at click time — passing it as a prop
                                // would force every caller to derive
                                // it; the store lookup is fast and
                                // local to this affordance.
                                const owningSite = useStore.getState().siteRag.sites.find(s => s.sedId === sed.id);
                                if (!owningSite) {
                                  toast?.('Could not resolve owning site for this SED.');
                                  return;
                                }
                                setRevertCandidate({
                                  variable: v,
                                  originalValue: originalProviderValue,
                                  siteId: owningSite.id,
                                  lineageError: revertLineageError,
                                });
                              }}
                              className="flex items-center gap-1 px-1.5 py-0.5 type-meta uppercase tracking-widest border border-border text-text-secondary hover:text-warning hover:border-warning focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-warning"
                              data-testid={`sed-variable-revert-${v.path}`}
                              aria-label={`Revert ${v.label} to original provider value`}
                              title={revertLineageError ? `Unresolved revert lineage: ${revertLineageError}` : `Revert "${v.label}" to ${String(originalProviderValue ?? 'original provider value')}${v.unit ? ' ' + v.unit : ''}`}
                            >
                              <RotateCcw className="w-2.5 h-2.5" /> Revert
                            </button>
                          )}
                          {/* "Accept value as correct" — positive twin of Flag
                              incorrect. One click records a non-mutating review
                              breadcrumb. Shown only when there's a real value to
                              endorse and the row isn't already accepted. */}
                          {v.value != null && v.value !== '' && !isAccepted && (
                            <button
                              type="button"
                              onClick={() => void handleAcceptCorrect(v)}
                              disabled={acceptingPath === v.path}
                              className="flex items-center gap-1 px-1.5 py-0.5 type-meta border border-border rounded-md text-text-secondary hover:text-success hover:border-success disabled:opacity-50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-success"
                              data-testid={`sed-variable-accept-correct-${v.path}`}
                              aria-label={`Accept ${v.label} value as correct`}
                              title="Confirm the auto-extracted value is correct (records a review; does not change the value)"
                            >
                              <Check className="w-2.5 h-2.5" /> Accept correct
                            </button>
                          )}
                          {/* LB3-1 — visible labeled "Amend" chip
                              replacing the white/40 12px icon. Matches
                              the DocChecklist ActionStrip's chip style
                              for visual consistency. */}
                          <button
                            type="button"
                            onClick={() => startFlagIncorrect(v)}
                            className="flex items-center gap-1 px-1.5 py-0.5 type-meta uppercase tracking-widest border border-border rounded-md text-text-secondary hover:text-warning hover:border-warning focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-warning"
                            data-testid={`sed-variable-flag-incorrect-${v.path}`}
                            aria-label={`Flag ${v.label} as incorrect extraction`}
                          >
                            <AlertTriangle className="w-2.5 h-2.5" /> Flag incorrect
                          </button>
                          {siteId && (
                            <button
                              type="button"
                              onClick={() => void refreshVariableSection(v)}
                              disabled={refreshingSectionPath === v.path}
                              className="flex items-center gap-1 px-1.5 py-0.5 type-meta uppercase tracking-widest border border-border rounded-md text-text-secondary hover:text-primary hover:border-primary disabled:opacity-50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
                              data-testid={`sed-variable-refresh-section-${v.path}`}
                              aria-label={`Re-fetch ${v.domainLabel} section`}
                              title={`Re-fetch provider data for ${v.domainLabel}`}
                            >
                              <RefreshCw className={`w-2.5 h-2.5 ${refreshingSectionPath === v.path ? 'animate-spin' : ''}`} />
                              Re-fetch section
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => startEdit(v)}
                            className="flex items-center gap-1 px-1.5 py-0.5 type-meta uppercase tracking-widest border border-border rounded-md text-text-secondary hover:text-primary hover:border-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
                            data-testid={`sed-variable-amend-${v.path}`}
                            aria-label={`Amend ${v.label}`}
                          >
                            <Edit3 className="w-2.5 h-2.5" /> Amend
                          </button>
                        </div>
                        );
                      })()}
                    </div>

                    {/* PRV-1 / PRV-2 — Provenance Per Variable lineage
                        strip. Inline, single-line summary of WHERE this
                        value came from for verified-from-doc /
                        -context / -report rows. Doc rows surface the
                        page + section + excerpt + an [Open at page]
                        button that mounts DocViewerDialog at the right
                        page (same plumbing as RefLink). Authored rows
                        surface the source-span quote + actor. Report-
                        derived rows surface the originating report
                        type + version.

                        Strip is omitted entirely when the row has no
                        projectable provenance (provider-only verified,
                        unavailable, anomalous without a doc backstop).
                        Rows WITH provenance keep the lineage drawer
                        affordance for the deep-history view; the strip
                        is the inline diligence shortcut.

                        R1 — pageNumber + sectionHeading are optional
                        and the strip omits each chunk individually
                        when missing (legacy SEDs pre-REF-LINEAGE).
                        R2/R3/R5 — excerpts are truncated at 200 chars
                        with the tooltip carrying the full text; the
                        ≥2nd ref lives behind a disclosure so dense
                        rows stay compact. */}
                    <VariableProvenanceStrip
                      variable={v}
                      resolveDocName={resolveDocName}
                      openDocViewerAt={openDocViewerAt}
                    />

                    {/* Citation_Traceability CT5 — full-chain lineage
                        drawer. Replaces the LB3-2 amendments-only
                        drawer with one that surfaces every kind of
                        source (amendments + supporting docs + provider
                        data + Site Context claims) newest-first.
                        Wrapper kept on the testid path the LB3 tests
                        rely on (`sed-variable-history-drawer-*`) so
                        the existing surface stays click-through; the
                        new lineage rows live INSIDE that wrapper. */}
                    {expandedHistoryPath === v.path && (
                      <div data-testid={`sed-variable-history-drawer-${v.path}`}>
                        <VariableLineageDrawer
                          variablePath={v.path}
                          label={v.label}
                          currentValue={v.value}
                          unit={v.unit}
                          sed={sed}
                          amendments={effectiveAmendmentsForBrowser}
                          siteContext={siteContext ?? null}
                          documents={documents}
                          testIdSuffix={v.path}
                          onOpenDocument={onOpenDocument}
                        />
                      </div>
                    )}

                    {/* Amendment form */}
                    {editingPath === v.path && (() => {
                      // AMP-1 — per-field error treatment. Required
                      // labels turn `text-danger` once the user has
                      // interacted with the form OR clicked Save while
                      // blockers existed. The inputs themselves carry
                      // a red border + `aria-invalid` so screen-readers
                      // catch the same state.
                      const reasonInvalid = editReason.trim().length === 0 && hasUserInteracted;
                      const sourceLabelInvalid = editSourceLabel.trim().length === 0 && hasUserInteracted;
                      const sourceUrlInvalid = editSourceType === 'external-link'
                        && editSourceUrl.trim().length === 0
                        && hasUserInteracted;
                      const buttonLabel = isSubmitting
                        ? 'Saving…'
                        : blockingFields.length === 0
                          ? 'Save Amendment'
                          : `Save (${blockingFields[0]} needed)`;
                      return (
                      <div className="mt-2 p-2 bg-state-hover border border-border-subtle space-y-2">
                        <div className="flex gap-2">
                          {(() => {
                            // "Add list from setup" — when the variable maps to a
                            // closed setup list (e.g. technology → reactor design
                            // class), amend via a picklist of those exact options
                            // rather than a free-text box. Everything else keeps
                            // the free-text input.
                            const options = sedVariableOptionsForValue(v.path, v.value);
                            if (options) {
                              return (
                                <select
                                  className="flex-1 text-[10px] font-mono px-2 py-1 border border-border bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus:ring-1 focus:ring-primary"
                                  value={editValue}
                                  onChange={e => { setEditValue(e.target.value); setHasUserInteracted(true); }}
                                  data-testid={`sed-variable-amend-select-${v.path}`}
                                  aria-label={`New value for ${v.label}`}
                                >
                                  {editValue === '' && <option value="" disabled>Select a value…</option>}
                                  {options.map(o => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                  ))}
                                </select>
                              );
                            }
                            return (
                              <input
                                className="flex-1 text-[10px] font-mono px-2 py-1 border border-border bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus:ring-1 focus:ring-primary"
                                value={editValue}
                                onChange={e => { setEditValue(e.target.value); setHasUserInteracted(true); }}
                                placeholder="New value"
                              />
                            );
                          })()}
                          {v.unit && <span className="type-body text-text-secondary self-center">{v.unit}</span>}
                        </div>
                        <div className="space-y-0.5">
                          <label
                            className={`type-meta font-bold uppercase tracking-widest ${reasonInvalid ? 'text-danger' : 'text-text-secondary'}`}
                            htmlFor={`amend-reason-${v.path}`}
                          >
                            Reason for change <span className="text-danger">*</span>
                          </label>
                          <textarea
                            id={`amend-reason-${v.path}`}
                            className={`w-full text-[10px] px-2 py-1 border bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus:ring-1 focus:ring-primary ${reasonInvalid ? 'border-danger' : 'border-border'}`}
                            value={editReason}
                            onChange={e => { setEditReason(e.target.value); setHasUserInteracted(true); }}
                            placeholder="Reason for change (required)"
                            rows={2}
                            aria-invalid={reasonInvalid}
                            aria-required="true"
                          />
                        </div>
                        <div className="flex gap-1">
                          {(['expert-judgment', 'site-visit', 'document', 'external-link'] as const).map(t => (
                            <button
                              key={t}
                              className={`flex items-center gap-1 type-meta px-2 py-1 border rounded-md transition-colors ${editSourceType === t ? 'selected-accent' : 'bg-card text-foreground border-border hover:bg-state-hover'}`}
                              onClick={() => { setEditSourceType(t); setHasUserInteracted(true); }}
                            >
                              {t === 'expert-judgment' && <User className="w-2.5 h-2.5" />}
                              {t === 'site-visit' && <MapPin className="w-2.5 h-2.5" />}
                              {t === 'document' && <Upload className="w-2.5 h-2.5" />}
                              {t === 'external-link' && <Link className="w-2.5 h-2.5" />}
                              {t.replace('-', ' ')}
                            </button>
                          ))}
                        </div>
                        {editSourceType === 'external-link' && (
                          <div className="space-y-0.5">
                            <label
                              className={`type-meta font-bold uppercase tracking-widest ${sourceUrlInvalid ? 'text-danger' : 'text-text-secondary'}`}
                              htmlFor={`amend-source-url-${v.path}`}
                            >
                              Source URL <span className="text-danger">*</span>
                            </label>
                            <input
                              id={`amend-source-url-${v.path}`}
                              className={`w-full text-[10px] px-2 py-1 border bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 ${sourceUrlInvalid ? 'border-danger' : 'border-border'}`}
                              value={editSourceUrl}
                              onChange={e => { setEditSourceUrl(e.target.value); setHasUserInteracted(true); }}
                              placeholder="https://..."
                              aria-invalid={sourceUrlInvalid}
                              aria-required="true"
                            />
                          </div>
                        )}
                        <div className="space-y-0.5">
                          <label
                            className={`type-meta font-bold uppercase tracking-widest ${sourceLabelInvalid ? 'text-danger' : 'text-text-secondary'}`}
                            htmlFor={`amend-source-${v.path}`}
                          >
                            Source description <span className="text-danger">*</span>
                          </label>
                          <input
                            id={`amend-source-${v.path}`}
                            className={`w-full text-[10px] px-2 py-1 border bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 ${sourceLabelInvalid ? 'border-danger' : 'border-border'}`}
                            value={editSourceLabel}
                            onChange={e => { setEditSourceLabel(e.target.value); setHasUserInteracted(true); }}
                            placeholder="e.g., 'Geotechnical report by Smith & Associates'"
                            aria-invalid={sourceLabelInvalid}
                            aria-required="true"
                          />
                        </div>
                        {/* AMP-1 — inline error strip that names every
                            blocker. Renders only AFTER the user has
                            interacted with the form so the fresh-open
                            state doesn't shout. Tooltip on the disabled
                            button carries the same message for users
                            who haven't started typing yet. */}
                        {blockerMessage && hasUserInteracted && (
                          <div
                            className="mt-2 px-2 py-1 type-body text-danger bg-danger/5 rounded-md border border-danger/20"
                            data-testid={`amend-validation-strip-${v.path}`}
                            role="alert"
                          >
                            ⚠ Can't save yet — {blockerMessage}
                          </div>
                        )}
                        <div className="flex gap-2 justify-end">
                          <button className="type-body px-3 py-1 text-text-muted hover:text-text-primary" onClick={cancelEdit}>
                            <X className="w-3 h-3 inline mr-1" />Cancel
                          </button>
                          <button
                            className="type-body px-3 py-1 bg-primary text-text-primary hover:bg-primary/90 disabled:opacity-50"
                            disabled={!isSubmittable}
                            aria-disabled={!isSubmittable}
                            title={isSubmittable
                              ? 'Save this amendment to the SED'
                              : blockerMessage ?? 'Saving…'}
                            onClick={() => submitAmendment(v)}
                            data-testid={`amend-save-button-${v.path}`}
                          >
                            <Check className="w-3 h-3 inline mr-1" />{buttonLabel}
                          </button>
                        </div>
                      </div>
                      );
                    })()}

                    {/* Show unconfirmed in-flight/interrupted save envelopes for exact-payload retry */}
                    {(() => {
                      void pendingEnvelopeRevision;
                      const scope = resolveEnvelopeScopeForVariable(v.path);
                      const unresolvedEnvs =
                        scope.actorUid && scope.sedId
                          ? readPendingSedSaveEnvelopes(scope).filter(
                              env =>
                                !(
                                  isSubmitting &&
                                  editingPath === v.path &&
                                  activeSubmissionOperationIdRef.current === env.operationId
                                ),
                            )
                          : [];
                      if (unresolvedEnvs.length === 0) return null;
                      return unresolvedEnvs.map((unresolvedEnv, idx) => (
                        <div
                          key={unresolvedEnv.operationId}
                          className="mt-1 flex items-center justify-between gap-2 type-meta text-warning bg-warning/15 px-2 py-1 border-l-2 border-warning/40"
                          data-testid={
                            idx === 0
                              ? `sed-variable-unresolved-save-${v.path}`
                              : `sed-variable-unresolved-save-${v.path}-${idx}`
                          }
                        >
                          <span>
                            Unconfirmed save ({String(unresolvedEnv.amendment.oldValue ?? '—')} → {String(unresolvedEnv.amendment.newValue ?? '—')}
                            {v.unit ? ` ${v.unit}` : ''} — {unresolvedEnv.amendment.reason} · {unresolvedEnv.operationId})
                          </span>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <button
                              type="button"
                              disabled={isSubmitting}
                              onClick={() => void retryUnresolvedEnvelope(v, unresolvedEnv)}
                              className="px-2 py-0.5 bg-primary text-text-primary hover:bg-primary/90 disabled:opacity-50"
                              data-testid={
                                idx === 0
                                  ? `sed-variable-retry-save-${v.path}`
                                  : `sed-variable-retry-save-${v.path}-${idx}`
                              }
                            >
                              Retry Save
                            </button>
                            <button
                              type="button"
                              disabled={isSubmitting}
                              onClick={() => discardUnresolvedEnvelope(v.path, unresolvedEnv.operationId)}
                              className="px-2 py-0.5 border border-border text-text-secondary hover:text-text-primary disabled:opacity-50"
                              data-testid={
                                idx === 0
                                  ? `sed-variable-discard-save-${v.path}`
                                  : `sed-variable-discard-save-${v.path}-${idx}`
                              }
                            >
                              Discard
                            </button>
                          </div>
                        </div>
                      ));
                    })()}

                    {unresolvedRefreshByPath[v.path] && (
                      <div
                        className="mt-1 flex items-center justify-between gap-2 type-meta text-danger bg-danger/10 px-2 py-1 border-l-2 border-danger/50"
                        data-testid={`sed-variable-refresh-required-${v.path}`}
                        role="alert"
                      >
                        <span>
                          {unresolvedRefreshByPath[v.path]!.message}
                          {unresolvedRefreshByPath[v.path]!.operationId
                            ? ` (${unresolvedRefreshByPath[v.path]!.operationId})`
                            : ''}
                        </span>
                        <button
                          type="button"
                          disabled={refreshingBaselinePath === v.path}
                          onClick={() => void handleRefreshAuthoritativeBaseline(v.path)}
                          className="px-2 py-0.5 border border-danger/40 text-danger hover:bg-danger/15 disabled:opacity-50 shrink-0"
                          data-testid={`sed-variable-refresh-baseline-${v.path}`}
                        >
                          {refreshingBaselinePath === v.path ? 'Refreshing…' : 'Refresh Evidence'}
                        </button>
                      </div>
                    )}

                    {/* Show pending amendment if exists */}
                    {v.pendingAmendment && editingPath !== v.path && (
                      <div className="mt-1 type-meta text-warning bg-warning/15 px-2 py-1 border-l-2 border-warning/40">
                        Pending: {String(v.pendingAmendment.oldValue)} → {String(v.pendingAmendment.newValue)} — {v.pendingAmendment.reason}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
      </div>

      {/* AMP-3 — Revert confirmation dialog. Surfaces ONLY when the
          user clicked a [↺ Revert] chip; the dialog is a destructive-
          action prompt that names BOTH the value to be restored
          (provider original) AND the current value that will be
          archived as historical. Cancel always available; confirming
          dispatches `revertSedAmendment` and closes the dialog on
          success. On failure the dialog stays open with a toast so
          the user can retry. */}
      {revertCandidate && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="amp-revert-dialog-title"
          data-testid="amp-revert-dialog"
        >
          <div className="bg-card border border-border p-4 max-w-md w-full space-y-3 mx-4">
            <div className="flex items-center gap-2">
              <RotateCcw className="w-4 h-4 text-warning" aria-hidden="true" />
              <h2 id="amp-revert-dialog-title" className="text-[11px] font-bold uppercase tracking-widest text-text-primary">
                Revert {revertCandidate.variable.label}?
              </h2>
            </div>
            {revertCandidate.lineageError ? (
              <p className="text-[10px] text-warning leading-relaxed" data-testid="amp-revert-lineage-error">
                Cannot revert {revertCandidate.variable.label}: original provider baseline could not be resolved from amendment lineage ({revertCandidate.lineageError}).
              </p>
            ) : (
              <p className="text-[10px] text-text-secondary leading-relaxed">
                The current value{' '}
                <span className="font-mono font-bold text-text-primary">
                  {String(revertCandidate.variable.value ?? '—')}
                  {revertCandidate.variable.unit ? ' ' + revertCandidate.variable.unit : ''}
                </span>
                {' '}will be archived as historical and replaced with the original provider value{' '}
                <span className="font-mono font-bold text-text-primary">
                  {String(revertCandidate.originalValue ?? '—')}
                  {revertCandidate.variable.unit ? ' ' + revertCandidate.variable.unit : ''}
                </span>.
                {' '}A new amendment record will track the revert in the audit chain — the original
                amendments stay in place as historical evidence.
              </p>
            )}
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                className="type-body px-3 py-1 text-text-muted hover:text-text-primary"
                onClick={() => setRevertCandidate(null)}
                disabled={isReverting}
                data-testid="amp-revert-cancel"
              >
                <X className="w-3 h-3 inline mr-1" />Cancel
              </button>
              <button
                type="button"
                className="type-body px-3 py-1 bg-warning text-text-primary hover:bg-warning/90 disabled:opacity-50"
                disabled={isReverting || Boolean(revertCandidate.lineageError)}
                onClick={async () => {
                  if (!revertCandidate) return;
                  setIsReverting(true);
                  try {
                    const result = await revertSedAmendment(revertCandidate.siteId, revertCandidate.variable.path);
                    if (!result.ok) {
                      toast?.(`Revert failed: ${result.error}`);
                      return;
                    }
                    toast?.(
                      result.applied === false
                        ? `Revert for "${revertCandidate.variable.label}" submitted for review.`
                        : `Reverted "${revertCandidate.variable.label}" to provider value.`,
                    );
                    setRevertCandidate(null);
                    if (result.applied !== false) {
                      onReloadSed?.();
                    }
                  } finally {
                    setIsReverting(false);
                  }
                }}
                data-testid="amp-revert-confirm"
              >
                <RotateCcw className="w-3 h-3 inline mr-1" />
                {isReverting ? 'Reverting…' : 'Revert'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
